import React, { useState } from "react"
import type { AxisLeaf } from "utils/tableModel"
import type { EngineOutputItem } from "types/engineOutput.types"
import type { DataTile } from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  useDashboardStoreApi,
} from "features/Dashboard/runtime/dashboardStore"
import { tileTableId } from "features/Dashboard/runtime/tileScript"
import { runTraceCheck } from "features/Dashboard/runtime/runTile"
import { useDashboardQueueContext } from "features/Dashboard/runtime/useTileRunner"
import { panelCategories } from "features/Dashboard/runtime/panels"
import {
  describeDashboardHelper,
  describeDashboardScript,
} from "features/Dashboard/runtime/traceNames"
import { TraceNumberPanel } from "features/Provenance"
import type { TraceRequest } from "features/Provenance"

/** A number a tile shows: its cell's leaves in the tile's view, and the number as shown. */
export interface TraceTarget {
  down: AxisLeaf
  across: AxisLeaf
  value: string
  /** Said with the cell when the tile shows the number otherwise than the table does. */
  note?: string
}

const MEASURE_NOTE =
  "This row is a measure the dashboard works out from the table's numbers; the trace shows the table it reads."

/**
 * The trace of a number a tile shows, as every tile opens it: `openTrace`
 * opens the trace panel for a cell, and `panel` is the panel to render
 * (nothing while closed). `repeatCategory` names the panel of a repeated
 * tile the number is in: its own table and script are traced.
 *
 * The view renames and reorders categories but keeps their ids, so the
 * leaves of the last result stand in for the shown ones; a measure row is
 * worked out here, not in the session. The check of the cell's calculation
 * runs the tile's script again on the dashboard's queue: only for a builder
 * tile (a script tile's own lines may not run twice) and a cell the session
 * made (not a measure). The panel reads the session's lineage on the same
 * queue, so the read never meets a tile's run. The names of the dashboard's
 * tiles are read when the trace opens, so an edit elsewhere renders nothing
 * here.
 */
export const useTileTrace = (
  tile: DataTile,
  repeatCategory?: string
): {
  openTrace: (target: TraceTarget) => void
  panel: React.ReactNode
} => {
  const projectId = useDashboardStore((s) => s.projectId)
  const instance = useDashboardStore((s) => s.session.instance)
  const preparation = useDashboardStore((s) => s.session.preparation)
  const retryTile = useDashboardStore((s) => s.retryTile)
  const storeApi = useDashboardStoreApi()
  const queue = useDashboardQueueContext()
  const [trace, setTrace] = useState<{
    request: TraceRequest
    describeHelper: (name: string) => string | undefined
    describeScript: (script: string) => string | undefined
    runCheck?: (script: string) => Promise<EngineOutputItem[]>
  } | null>(null)

  const openTrace = ({ down, across, value, note }: TraceTarget) => {
    const { document, tiles, codebook } = storeApi.getState()
    const runtime = tiles[tile.id]
    // A panel runs its own table, numbered by its place among the repeat
    // variable's categories (from 1), as the plan numbers it.
    let table = tileTableId(tile.id)
    let ran = { script: runtime?.script, model: runtime?.model }
    if (repeatCategory !== undefined) {
      const variable = tile.repeatBy?.variable
      const k = variable
        ? panelCategories(codebook, variable).indexOf(repeatCategory)
        : -1
      const panel = runtime?.panels?.[repeatCategory]
      if (k < 0 || !panel) return
      table = tileTableId(tile.id, k + 1)
      ran = { script: panel.script, model: panel.model }
    }
    const rawLeaf = (axis: "down" | "across", leaf: AxisLeaf): AxisLeaf =>
      ran.model?.[axis].leaves.find(
        (l) => l.id === leaf.id && l.stat === leaf.stat
      ) ?? leaf
    const measure = [down, across].some((l) =>
      tile.measures.some((m) => m.id === l.id)
    )
    const request: TraceRequest = {
      table,
      down: rawLeaf("down", down),
      across: rawLeaf("across", across),
      value,
      title: tile.title,
      tableScript: ran.script,
      transposed: tile.type === "table" ? tile.visual.transposed : false,
      note: measure ? MEASURE_NOTE : note,
    }
    setTrace({
      request,
      describeHelper: describeDashboardHelper(document),
      describeScript: describeDashboardScript(document),
      runCheck:
        queue && tile.source.kind === "builder" && !measure
          ? (script) => runTraceCheck(queue, tile.id, script)
          : undefined,
    })
  }

  // Mounted only while open: it reads the lineage and the questionnaire.
  const panel = trace && (
    <TraceNumberPanel
      request={trace.request}
      onClose={() => setTrace(null)}
      instance={instance}
      projectId={projectId}
      describeHelper={trace.describeHelper}
      describeScript={trace.describeScript}
      preparation={preparation}
      runCheck={trace.runCheck}
      runExclusive={queue?.runExclusive}
      onRunAgain={() => {
        setTrace(null)
        retryTile(tile.id)
      }}
    />
  )

  return { openTrace, panel }
}
